(() => {
  if (window.KervelanCollectionMobileLoader) return;

  const entry = document.currentScript;
  const source = entry?.dataset.kvCollectionMobileRuntime;
  if (!source) return;

  const mobile = window.matchMedia('(max-width: 749px)');
  const editor = entry.dataset.kvCollectionEditor === 'true';
  const controlSelector = '.kv-mobile-collection-controls__filter, .kv-mobile-collection-controls__sort';
  const intentSelector = `${controlSelector}, .kv-mobile-collection-controls__sort-fallback`;
  const bypass = new WeakSet();
  let pendingTrigger;
  let runtimePromise;
  let failures = 0;
  let frame = 0;
  let idle = 0;
  let timer = 0;

  const ready = () => window.__KervelanMobileCollectionReady === true;
  const cancelScheduled = () => {
    if (frame) window.cancelAnimationFrame(frame);
    if (idle) window.cancelIdleCallback?.(idle);
    if (timer) window.clearTimeout(timer);
    frame = idle = timer = 0;
  };

  const ensure = () => {
    cancelScheduled();
    if (!mobile.matches || ready()) return Promise.resolve();
    if (runtimePromise) return runtimePromise;

    const url = new URL(source, document.baseURI);
    if (failures) url.searchParams.set('kv_mobile_retry', String(failures));
    runtimePromise = import(url.href)
      .then(() => {
        if (!ready()) throw new Error('Mobile collection runtime is unavailable');
      })
      .catch((error) => {
        runtimePromise = undefined;
        failures += 1;
        console.warn('[KERVELAN] Mobile collection runtime load failed', error);
        throw error;
      });
    return runtimePromise;
  };

  const schedule = () => {
    if (!mobile.matches || ready() || runtimePromise || frame || idle || timer) return;
    if (editor) {
      ensure().catch(() => {});
      return;
    }
    if (document.readyState !== 'complete') return;

    frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (!mobile.matches) return;
        if (window.requestIdleCallback) {
          idle = window.requestIdleCallback(() => {
            idle = 0;
            ensure().catch(() => {});
          }, { timeout: 1500 });
        } else {
          timer = window.setTimeout(() => {
            timer = 0;
            ensure().catch(() => {});
          }, 300);
        }
      });
    });
  };

  const getControl = (target, selector) => {
    if (!(target instanceof Element) || !mobile.matches) return null;
    const control = target.closest(selector);
    return control?.closest('collection-component.kervelan-collection') ? control : null;
  };

  const warm = (event) => {
    if (getControl(event.target, intentSelector)) ensure().catch(() => {});
  };

  const fallback = (trigger) => {
    if (!trigger.isConnected || !mobile.matches) return;
    if (trigger.matches('.kv-mobile-collection-controls__sort')) {
      trigger.closest('.kv-mobile-collection-controls__sort-slot')
        ?.querySelector('.kv-mobile-collection-controls__sort-fallback')?.focus();
      return;
    }
    bypass.add(trigger);
    try { trigger.click(); } finally { bypass.delete(trigger); }
  };

  window.addEventListener('click', (event) => {
    const trigger = getControl(event.target, controlSelector);
    if (!(trigger instanceof HTMLButtonElement) || ready() || bypass.has(trigger)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const alreadyPending = Boolean(pendingTrigger);
    pendingTrigger = trigger;
    if (alreadyPending) return;

    let timeout;
    Promise.race([
      ensure(),
      new Promise((_, reject) => {
        timeout = window.setTimeout(() => reject(new Error('Mobile collection runtime timed out')), 2500);
      }),
    ]).then(() => {
      if (!pendingTrigger.isConnected || !mobile.matches) return;
      if (!window.KervelanMobileCollection?.handleTrigger(pendingTrigger)) fallback(pendingTrigger);
    }).catch(() => fallback(pendingTrigger)).finally(() => {
      window.clearTimeout(timeout);
      pendingTrigger = undefined;
    });
  }, true);

  document.addEventListener('pointerdown', warm, { capture: true, passive: true });
  document.addEventListener('pointerover', warm, { capture: true, passive: true });
  document.addEventListener('focusin', warm, true);
  window.addEventListener('load', schedule, { once: true });
  window.addEventListener('pageshow', schedule);
  document.addEventListener('shopify:section:load', schedule);
  mobile.addEventListener?.('change', () => {
    if (mobile.matches) schedule();
    else cancelScheduled();
  });
  window.KervelanCollectionMobileLoader = { ensure };
  schedule();
})();
